/**
 * Allowance calculator math. Prices are in the offer currency (cents); AI cost is in USD,
 * because that is what the model provider charges. `fxPerUsd` is offer-currency units per 1 USD
 * (the Billing FX table's direction: AUD 1.43 means US$1 = A$1.43).
 */

export interface PlanResult {
  /** Units to provision: leads a month (LeadGen) or requests per seat a month (Loxley). */
  units: number;
  /** What the customer pays a month, in offer-currency cents. */
  priceCents: number;
  /** Model cost if every unit is used, in USD. */
  fullUseCostUsd: number;
  /** Gross margin at full use, percent of the price. null without a price. */
  marginPct: number | null;
  /** Cost limit to set on Organization AI: full-use cost plus headroom, whole US cents. */
  capCents: number;
  /** Most units the price can carry at the target margin. null until a cost per unit is known. */
  maxUnitsAtMargin: number | null;
}

function marginPct(revenueUsd: number, costUsd: number): number | null {
  return revenueUsd > 0 ? ((revenueUsd - costUsd) / revenueUsd) * 100 : null;
}

// Floating point turns 1.56 x 1.25 x 100 into 195.00000000000003; round inside a millionth.
const EPSILON = 1e-6;

function capCents(costUsd: number, headroomPct: number): number {
  return Math.max(0, Math.ceil(costUsd * (1 + headroomPct / 100) * 100 - EPSILON));
}

/**
 * LeadGen: the rate card (e.g. 55 leads for 49.00) fixes a price per lead, so a new price
 * buys price / price-per-lead leads, rounded down.
 */
export function leadgenPlan(input: {
  rateCardPriceCents: number;
  rateCardLeads: number;
  priceCents: number;
  costPerLeadUsd: number;
  fxPerUsd: number;
  targetMarginPct: number;
  headroomPct: number;
}): PlanResult {
  const perLead = input.rateCardLeads > 0 ? input.rateCardPriceCents / input.rateCardLeads : 0;
  const units = perLead > 0 ? Math.floor(input.priceCents / perLead + EPSILON) : 0;
  const revenueUsd = input.fxPerUsd > 0 ? input.priceCents / 100 / input.fxPerUsd : 0;
  const fullUseCostUsd = units * input.costPerLeadUsd;
  return {
    units,
    priceCents: input.priceCents,
    fullUseCostUsd,
    marginPct: marginPct(revenueUsd, fullUseCostUsd),
    capCents: capCents(fullUseCostUsd, input.headroomPct),
    maxUnitsAtMargin:
      input.costPerLeadUsd > 0 ? Math.floor((revenueUsd * (1 - input.targetMarginPct / 100)) / input.costPerLeadUsd + EPSILON) : null,
  };
}

/**
 * Loxley: priced per seat, and one cost limit covers all seats together. `units` is the
 * requests per seat a month the limit is sized for.
 */
export function loxleyPlan(input: {
  seatPriceCents: number;
  seats: number;
  requestsPerSeat: number;
  costPerRequestUsd: number;
  fxPerUsd: number;
  targetMarginPct: number;
  headroomPct: number;
}): PlanResult {
  const priceCents = input.seatPriceCents * input.seats;
  const revenueUsd = input.fxPerUsd > 0 ? priceCents / 100 / input.fxPerUsd : 0;
  const fullUseCostUsd = input.seats * input.requestsPerSeat * input.costPerRequestUsd;
  const seatRevenueUsd = input.fxPerUsd > 0 ? input.seatPriceCents / 100 / input.fxPerUsd : 0;
  return {
    units: input.requestsPerSeat,
    priceCents,
    fullUseCostUsd,
    marginPct: marginPct(revenueUsd, fullUseCostUsd),
    capCents: capCents(fullUseCostUsd, input.headroomPct),
    maxUnitsAtMargin:
      input.costPerRequestUsd > 0
        ? Math.floor((seatRevenueUsd * (1 - input.targetMarginPct / 100)) / input.costPerRequestUsd + EPSILON)
        : null,
  };
}

/**
 * Mailbox AI: a flat monthly price with no unit sold. The limit is sized for the emails a
 * month the shared mailbox is expected to sort; `units` is that volume.
 */
export function mailboxPlan(input: {
  priceCents: number;
  emailsPerMonth: number;
  costPerEmailUsd: number;
  fxPerUsd: number;
  targetMarginPct: number;
  headroomPct: number;
}): PlanResult {
  const revenueUsd = input.fxPerUsd > 0 ? input.priceCents / 100 / input.fxPerUsd : 0;
  const fullUseCostUsd = input.emailsPerMonth * input.costPerEmailUsd;
  return {
    units: input.emailsPerMonth,
    priceCents: input.priceCents,
    fullUseCostUsd,
    marginPct: marginPct(revenueUsd, fullUseCostUsd),
    capCents: capCents(fullUseCostUsd, input.headroomPct),
    maxUnitsAtMargin:
      input.costPerEmailUsd > 0
        ? Math.floor((revenueUsd * (1 - input.targetMarginPct / 100)) / input.costPerEmailUsd + EPSILON)
        : null,
  };
}
